import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FiltersPanelWrapper,
  FiltersPanelHeader,
  FiltersPanelBody,
  FiltersSection,
  FilterChipsWrapper,
  FilterChip,
  FiltersFooter,
  SmallBtn,
  CloseXBtn,
} from "./styles";
import { getSafe } from "../../utils";
import { useFetchCities } from "../../hooks/useFetchCities";
import { useFetchTags } from "../../hooks/useFetchTags";

/**
 * Props
 * open (bool)
 * onClose() -> void
 * selectedCities (array of slugs)
 * selectedTags (array of slugs)
 * onChangeCities(nextArray)
 * onChangeTags(nextArray)
 * onApply(citiesArray, tagsArray)
 * onCancel() -> void (resets local working copy in parent)
 */
const SearchFiltersPanel = ({
  open,
  onClose,
  selectedCities = [],
  selectedTags = [],
  onChangeCities,
  onChangeTags,
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { data: cities = [] } = useFetchCities();
  const { data: tags = [] } = useFetchTags();
  const { local } = useSelector((s) => s.Local || {});
  const panelRef = useRef();

  // Close on ESC
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape" && open) onClose?.();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Click outside to close
  useEffect(() => {
    function handleClick(e) {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const triggerAttr = e.target.getAttribute("data-filter-trigger");
        if (!triggerAttr) onClose?.();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onClose]);

  const toggleIn = (value, arr, cb) => {
    if (!cb) return;
    if (arr.includes(value)) {
      cb(arr.filter((v) => v !== value));
    } else {
      cb([...arr, value]);
    }
  };

  if (!open) return null;

  return (
    <FiltersPanelWrapper ref={panelRef} aria-label={t("filters", "Filters")}>
      <FiltersPanelHeader>
        <h2>{t("filterBy")}</h2>
        <CloseXBtn onClick={onClose} aria-label={t("close")}>
          ×
        </CloseXBtn>
      </FiltersPanelHeader>
      <FiltersPanelBody>
        <FiltersSection>
          <h3>
            {t("destinations")} ({selectedCities.length})
          </h3>
          <FilterChipsWrapper>
            {(cities || []).map((c) => (
              <FilterChip
                key={c.slug}
                $active={selectedCities.includes(c.slug)}
                onClick={() => toggleIn(c.slug, selectedCities, onChangeCities)}
              >
                {getSafe(() => c.title[local]) || c.slug}
              </FilterChip>
            ))}
          </FilterChipsWrapper>
        </FiltersSection>
        <FiltersSection>
          <h3>
            {t("preferences")} ({selectedTags.length})
          </h3>
          <FilterChipsWrapper>
            {(tags || []).map((tag) => (
              <FilterChip
                key={tag.slug}
                $active={selectedTags.includes(tag.slug)}
                onClick={() => toggleIn(tag.slug, selectedTags, onChangeTags)}
              >
                {getSafe(() => tag.name[local]) || tag.slug}
              </FilterChip>
            ))}
          </FilterChipsWrapper>
        </FiltersSection>
      </FiltersPanelBody>
      <FiltersFooter>
        <SmallBtn
          isSecondary
          type="button"
          onClick={() => {
            onCancel?.();
          }}
        >
          {t("cancel")}
        </SmallBtn>
        <SmallBtn
          type="button"
          onClick={() => {
            onApply?.(selectedCities, selectedTags);
            onClose?.();
          }}
          data-apply="true"
        >
          {t("applyFilters")}
        </SmallBtn>
      </FiltersFooter>
    </FiltersPanelWrapper>
  );
};

export default SearchFiltersPanel;
